/**
 * PR #755 — Canonical provider presets.
 *
 * Central, server-authoritative metadata for every supported external provider.
 * Presets contain only non-secret, stable configuration: base URL, endpoints,
 * auth scheme, default profile, timeout, retry policy and streaming support.
 *
 * Presets intentionally do NOT freeze upstream model IDs. The `defaultModel` and
 * `supportedProfiles` are Smart Shikka logical profile IDs; the provider
 * adapter/runtime maps each logical profile to an upstream model ID at call
 * time using `modelMapping` and/or server-backed model discovery. Discovery
 * results are cached with `modelDiscoveryTtlMs` and stored in `discoveredModels`
 * by the runtime — never treated as a static authority.
 *
 * The renderer receives these presets so it can prefill all non-secret fields;
 * the secret/API key field is always blank/masked and stored only on the server
 * or in the desktop secure backend.
 */
const OPENAI_COMPATIBLE_CHAT = "/v1/chat/completions";
const OPENAI_COMPATIBLE_MODELS = "/v1/models";
const DEFAULT_RETRY = { maxRetries: 3, baseDelayMs: 500, maxDelayMs: 8000 };
const DEFAULT_MODEL_DISCOVERY_TTL_MS = 5 * 60 * 1000;
const PRESETS = [
    {
        id: "gateway",
        displayName: "Qwen / Smart Shikka Gateway",
        providerType: "gateway",
        apiFamily: "qwen",
        baseUrl: "",
        authScheme: "bearer",
        authHeader: "Authorization",
        chatEndpoint: OPENAI_COMPATIBLE_CHAT,
        modelsEndpoint: OPENAI_COMPATIBLE_MODELS,
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant", "core-reasoning"],
        modelMapping: { "fast-assistant": "qwen3.5:4b", "core-reasoning": "qwen3.5:9b-q4_K_M" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        // CPU-only hosts need ≥60s for cold model load; 120s allows margin.
        // Interactive warm responses on GPU finish in <5s, but CPU inference
        // at ~0.3 tokens/sec makes this provider non-default for interactive chat.
        timeoutMs: 120000,
        connectTimeoutMs: 5000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        // Eligible as fallback but NOT recommended as interactive primary on CPU-only hosts.
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: true,
        requiresApiKey: true,
        maxOutputTokens: 4096,
        description: "Local/remote Smart Shikka gateway for Qwen 4B/9B. CPU-only hosts: use as offline/private fallback, not interactive primary.",
    },
    {
        id: "deepseek",
        displayName: "DeepSeek",
        providerType: "deepseek",
        apiFamily: "deepseek",
        baseUrl: "https://api.deepseek.com",
        apiVersion: "v1",
        authScheme: "bearer",
        authHeader: "Authorization",
        chatEndpoint: OPENAI_COMPATIBLE_CHAT,
        modelsEndpoint: OPENAI_COMPATIBLE_MODELS,
        healthStrategy: "models",
        defaultModel: "deepseek-v4-flash",
        supportedProfiles: ["deepseek-v4-flash", "deepseek-v4-pro"],
        // Canonical upstream ids from the live /models list — request `deepseek-v4-flash`
        // is an accepted alias but responses echo `deepseek-flash`; the pro tier is
        // canonical as `deepseek-v4-pro`.
        modelMapping: { "deepseek-v4-flash": "deepseek-flash", "deepseek-v4-pro": "deepseek-v4-pro" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        // DeepSeek cloud API responds in <2s; 30s is generous and prevents
        // long-hanging requests if the network degrades.
        timeoutMs: 30000,
        connectTimeoutMs: 10000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: true,
        requiresApiKey: true,
        maxOutputTokens: 8192,
        description: "DeepSeek OpenAI-compatible API.",
    },
    {
        id: "openai",
        displayName: "OpenAI",
        providerType: "openai",
        apiFamily: "openai",
        baseUrl: "https://api.openai.com",
        apiVersion: "v1",
        authScheme: "bearer",
        authHeader: "Authorization",
        chatEndpoint: OPENAI_COMPATIBLE_CHAT,
        modelsEndpoint: OPENAI_COMPATIBLE_MODELS,
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant", "core-reasoning"],
        modelMapping: { "fast-assistant": "gpt-4o-mini", "core-reasoning": "gpt-4o" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        timeoutMs: 120000,
        connectTimeoutMs: 10000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: false,
        requiresApiKey: true,
        maxOutputTokens: 4096,
        description: "OpenAI API (requires separate API key agreement).",
    },
    {
        id: "anthropic",
        displayName: "Anthropic",
        providerType: "anthropic",
        apiFamily: "anthropic",
        baseUrl: "https://api.anthropic.com",
        apiVersion: "v1",
        authScheme: "x-api-key",
        authHeader: "x-api-key",
        chatEndpoint: "/v1/messages",
        modelsEndpoint: "/v1/models",
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant", "core-reasoning"],
        modelMapping: { "fast-assistant": "claude-3-5-sonnet-20241022", "core-reasoning": "claude-3-5-sonnet-20241022" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        timeoutMs: 120000,
        connectTimeoutMs: 10000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: false,
        requiresApiKey: true,
        maxOutputTokens: 8192,
        description: "Anthropic Claude API.",
    },
    {
        id: "gemini",
        displayName: "Google Gemini",
        providerType: "gemini",
        apiFamily: "gemini",
        baseUrl: "https://generativelanguage.googleapis.com",
        apiVersion: "v1beta",
        authScheme: "x-goog-api-key",
        authHeader: "x-goog-api-key",
        chatEndpoint: "/v1beta/models/{model}:generateContent",
        modelsEndpoint: "/v1beta/models",
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant", "core-reasoning"],
        modelMapping: { "fast-assistant": "gemini-1.5-flash", "core-reasoning": "gemini-1.5-pro" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        timeoutMs: 120000,
        connectTimeoutMs: 10000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: false,
        requiresApiKey: true,
        maxOutputTokens: 8192,
        description: "Google Gemini API.",
    },
    {
        id: "groq",
        displayName: "Groq",
        providerType: "openai_compatible",
        apiFamily: "groq",
        baseUrl: "https://api.groq.com/openai",
        apiVersion: "v1",
        authScheme: "bearer",
        authHeader: "Authorization",
        chatEndpoint: OPENAI_COMPATIBLE_CHAT,
        modelsEndpoint: OPENAI_COMPATIBLE_MODELS,
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant"],
        modelMapping: { "fast-assistant": "llama-3.1-70b-versatile" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        timeoutMs: 120000,
        connectTimeoutMs: 10000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: false,
        requiresApiKey: true,
        maxOutputTokens: 4096,
        description: "Groq OpenAI-compatible API.",
    },
    {
        id: "openrouter",
        displayName: "OpenRouter",
        providerType: "openai_compatible",
        apiFamily: "openrouter",
        baseUrl: "https://openrouter.ai/api",
        apiVersion: "v1",
        authScheme: "bearer",
        authHeader: "Authorization",
        chatEndpoint: OPENAI_COMPATIBLE_CHAT,
        modelsEndpoint: OPENAI_COMPATIBLE_MODELS,
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant"],
        modelMapping: { "fast-assistant": "openai/gpt-4o-mini" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        timeoutMs: 120000,
        connectTimeoutMs: 10000,
        retryPolicy: DEFAULT_RETRY,
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: true,
        dataClassification: "internal",
        enabled: false,
        requiresApiKey: true,
        maxOutputTokens: 4096,
        description: "OpenRouter OpenAI-compatible API.",
    },
    {
        id: "ollama",
        displayName: "Local / Ollama",
        providerType: "local_model",
        apiFamily: "ollama",
        baseUrl: "http://localhost:11434",
        apiVersion: "v1",
        authScheme: "none",
        authHeader: "",
        chatEndpoint: "/api/chat",
        modelsEndpoint: "/api/tags",
        healthStrategy: "models",
        defaultModel: "fast-assistant",
        supportedProfiles: ["fast-assistant", "core-reasoning"],
        modelMapping: { "fast-assistant": "llama3.1", "core-reasoning": "llama3.1:70b" },
        modelDiscoveryTtlMs: DEFAULT_MODEL_DISCOVERY_TTL_MS,
        timeoutMs: 120000,
        connectTimeoutMs: 5000,
        retryPolicy: { maxRetries: 1, baseDelayMs: 500, maxDelayMs: 1000 },
        streaming: true,
        advancedOverrideAllowed: true,
        fallbackEligible: false,
        dataClassification: "internal",
        enabled: false,
        requiresApiKey: false,
        maxOutputTokens: 4096,
        description: "Ollama or other local OpenAI-compatible endpoint.",
    },
];
const PRESET_BY_ID = new Map(PRESETS.map((p) => [p.id, p]));
export function getCanonicalProviderPresets() {
    return PRESETS;
}
export function getCanonicalProviderPreset(providerId) {
    return PRESET_BY_ID.get(providerId);
}
export function listCanonicalProviderPresetIds() {
    return PRESETS.map((p) => p.id);
}
//# sourceMappingURL=providerPreset.js.map